import { startAppListening } from "Store/listenerMiddleware";

import { projectCreationActions } from "./projectCreation.reducer";
import {
  selectGithubOrganizations,
  selectSelectedGithubOrganizationId,
  selectSelectedGithubRepository,
  selectSelectedGithubRepositoryDefaultBranch,
  selectSelectedGithubBranch,
  selectVerificationType
} from "./projectCreation.selectors";
import { onboardingTypes, verificationTypes } from "./projectCreation.types";

import type { GithubOrganization } from "./projectCreation.types";
import {
  fetchGithubRepositories,
  fetchGithubBranches,
  fetchVerificationType
} from "./thunks";

/**
 * Listeners for the project creation flow.
 *
 * These replace useEffect chains in components for async coordination:
 * - Auto-selecting single options
 * - Cascading data fetches (org -> repos -> branches)
 *
 * Pattern: Each listener uses cancelActiveListeners() to prevent race conditions.
 */

// ============================================================================
// GitHub Organization Flow
// ============================================================================

/**
 * The organization to pick when there is only one to pick, or `null` when the
 * choice is the user's to make or has already been made.
 */
const singleOrganizationToSelect = (
  organizations: GithubOrganization[],
  currentSelection: string | null
): GithubOrganization | null => {
  // Only auto-select if there's exactly one organization
  if (organizations.length !== 1) return null;

  // Don't override existing selection
  if (currentSelection) return null;

  return organizations[0];
};

/**
 * Auto-select single GitHub organization after fetch.
 *
 * When organizations are fetched and there's only one, auto-select it.
 * This removes the need for a useEffect in Repository.tsx.
 */
startAppListening({
  actionCreator: projectCreationActions.setGithubOrganizations,
  effect: (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { organizationId, organizations } = action.payload;

    const state = listenerApi.getState();
    const org = singleOrganizationToSelect(
      organizations,
      selectSelectedGithubOrganizationId(state, { organizationId })
    );

    if (!org) return;

    // Use org.id (internal record ID), not gh_installation_id (GitHub's ID).
    // The API expects the internal ID for fetching repos and branches.
    listenerApi.dispatch(
      projectCreationActions.setSelectedGithubOrganization({
        organizationId,
        installationId: org.id,
        label: org.gh_organization_name
      })
    );
  }
});

/**
 * Auto-select the single GitHub organization again when a migrate flow starts
 * on a list it already has.
 *
 * The listener above is the only thing that starts the organization -> repos ->
 * branch cascade, and it runs off the fetch. `resetOnboardingState` keeps the
 * organization list and the "already fetched" flag — that pair is the answer to
 * "is the App installed", not an answer the user typed — so the second attempt
 * finds the fetch skipped by its own `condition`, the cascade never restarts,
 * and the page opens with three empty selects where the first attempt had them
 * filled in.
 *
 * Every entry to repository selection writes this type, from the `?onboardingType=`
 * the page is linked with or from the correction in `Routes`, so it marks the
 * start of the flow whether or not anything was fetched. It cannot fire on the
 * provider list: nothing there is a migrate flow until the user has left for
 * this page.
 */
startAppListening({
  actionCreator: projectCreationActions.setOnboardingType,
  effect: (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { organizationId, onboardingType } = action.payload;

    if (onboardingType !== onboardingTypes.migrate) return;

    const state = listenerApi.getState();
    const org = singleOrganizationToSelect(
      selectGithubOrganizations(state, { organizationId }),
      selectSelectedGithubOrganizationId(state, { organizationId })
    );

    if (!org) return;

    listenerApi.dispatch(
      projectCreationActions.setSelectedGithubOrganization({
        organizationId,
        installationId: org.id,
        label: org.gh_organization_name
      })
    );
  }
});

// ============================================================================
// GitHub Repository Flow
// ============================================================================

/**
 * Fetch repositories when a GitHub organization is selected.
 *
 * This cascades the selection flow: org selected -> fetch repos.
 */
startAppListening({
  actionCreator: projectCreationActions.setSelectedGithubOrganization,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { organizationId, installationId } = action.payload;

    // Only fetch if we have a valid selection
    if (!installationId) return;

    await listenerApi.dispatch(
      fetchGithubRepositories({ organizationId, installationId })
    );
  }
});

/**
 * Auto-select single repository after fetch.
 *
 * When repositories are fetched and there's only one, auto-select it.
 */
startAppListening({
  actionCreator: projectCreationActions.setGithubRepositories,
  effect: (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { organizationId, repositories } = action.payload;

    // Only auto-select if there's exactly one repository
    if (repositories.length !== 1) return;

    const state = listenerApi.getState();
    const currentSelection = selectSelectedGithubRepository(state, {
      organizationId
    });

    // Don't override existing selection
    if (currentSelection) return;

    const repo = repositories[0];
    listenerApi.dispatch(
      projectCreationActions.setSelectedGithubRepository({
        organizationId,
        repository: repo.name,
        defaultBranch: repo.default_branch
      })
    );
  }
});

// ============================================================================
// GitHub Branch Flow
// ============================================================================

/**
 * Fetch branches when a GitHub repository is selected.
 *
 * This cascades the selection flow: repo selected -> fetch branches.
 */
startAppListening({
  actionCreator: projectCreationActions.setSelectedGithubRepository,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { organizationId, repository } = action.payload;

    // Only fetch if we have a valid selection
    if (!repository) return;

    const state = listenerApi.getState();
    const installationId = selectSelectedGithubOrganizationId(state, {
      organizationId
    });

    if (!installationId) return;

    await listenerApi.dispatch(
      fetchGithubBranches({
        organizationId,
        installationId,
        repositoryName: repository
      })
    );
  }
});

/**
 * Auto-select branch after fetch.
 *
 * Priority:
 * 1. If there's a default branch and it exists in the list, select it
 * 2. If there's exactly one branch, select it
 * 3. Otherwise, don't auto-select (user must choose)
 */
startAppListening({
  actionCreator: projectCreationActions.setGithubBranches,
  effect: (action, listenerApi) => {
    const { organizationId, branches } = action.payload;

    if (branches.length === 0) return;

    const state = listenerApi.getState();
    const currentSelection = selectSelectedGithubBranch(state, {
      organizationId
    });

    // Don't override existing selection
    if (currentSelection) return;

    const defaultBranch = selectSelectedGithubRepositoryDefaultBranch(state, {
      organizationId
    });

    // Try to select the default branch if it exists in the list
    // Fall through to single-branch case if default branch isn't found
    const defaultBranchMatch = defaultBranch
      ? branches.find(b => b.name === defaultBranch)?.name
      : null;

    const branchToSelect =
      defaultBranchMatch ?? (branches.length === 1 ? branches[0].name : null);

    if (branchToSelect) {
      listenerApi.dispatch(
        projectCreationActions.setSelectedGithubBranch({
          organizationId,
          branch: branchToSelect
        })
      );
    }
  }
});

// ============================================================================
// Verification Flow
// ============================================================================

/**
 * Note: The verification type fetch remains in Routes.tsx because it needs
 * to happen on page mount before any onboarding type is selected.
 * This is a valid "on mount" effect that can't easily be moved to listeners
 * without adding complexity (e.g., listening for router location changes).
 *
 * However, we also trigger it when onboarding type changes as a safety net.
 */
startAppListening({
  actionCreator: projectCreationActions.setOnboardingType,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const { organizationId } = action.payload;

    const state = listenerApi.getState();
    const verificationType = selectVerificationType(state, { organizationId });

    // Only fetch if verification type hasn't been determined yet
    if (verificationType !== verificationTypes.default) return;

    await listenerApi.dispatch(fetchVerificationType({ organizationId }));
  }
});
